package top.wcpe.allincore.ui.runtime.ir.request

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.ui.runtime.ir.state.StateMachineExecutor
import top.wcpe.allincore.ui.runtime.ir.state.StateRecord

/**
 * 已发起但尚未收到回包的请求登记项。
 *
 * D2-b 起支持两种形态（以 [isStateMachine] 区分）：
 * - 旧形态：持有 success/fail/timeout 3 段 callback body 与 paramName，由 [RequestCallbacks] 直接执行。
 * - 状态机形态：持有 [StmtNode.StateMachine] + 当前 transition 的状态号、bindVarName、tailExpr 与
 *   [StateRecord] 快照，由 [StateMachineExecutor] 投递。
 *
 * 设计取舍：单类双形态 vs sealed 拆分 — 当前两种形态的字段数量 / 调用面均较小，保留现状。
 */
class PendingRequest(
    val requestId: String,
    val deadline: Long,
    val successBody: List<StmtNode>?,
    val successParam: String?,
    val failBody: List<StmtNode>?,
    val failParam: String?,
    val timeoutBody: List<StmtNode>?,
    val timeoutParam: String?,
    val stateMachine: Boolean,
    val machine: StmtNode.StateMachine?,
    val record: StateRecord?,
    val successNextStateId: Int,
    val failureNextStateId: Int,
    val timeoutNextStateId: Int,
    val bindVarName: String?,
    val tailExpr: ExprNode?,
    val netMode: Boolean,
    /**
     * 发起 request 时外层局部变量的按值快照（[EvalContext.snapshotVars]）。
     *
     * 仅旧 callback-body 形态使用：回调异步执行时外层 var 帧已 pop，
     * LocalRef 沿 var 帧栈自顶向下查找会失败（"未声明的局部变量"）。
     * 把快照注入回调执行 ctx 的 var 帧栈，让回调内能引用外层局部变量，
     * 与 DSL 语法上"回调看起来像闭包 lambda"的直觉一致。
     *
     * 状态机形态有自己的 [StateRecord] 快照机制，不使用本字段（保持空 map）。
     */
    val capturedVars: Map<String, Any?> = emptyMap(),
) {
    /** 旧 callback-body 形态构造器。 */
    constructor(
        requestId: String,
        successBody: List<StmtNode>,
        successParam: String?,
        failBody: List<StmtNode>,
        failParam: String?,
        timeoutBody: List<StmtNode>,
        timeoutParam: String?,
        netMode: Boolean,
        deadline: Long,
        capturedVars: Map<String, Any?> = emptyMap(),
    ) : this(
        requestId = requestId,
        deadline = deadline,
        successBody = successBody,
        successParam = successParam ?: "",
        failBody = failBody,
        failParam = failParam ?: "",
        timeoutBody = timeoutBody,
        timeoutParam = timeoutParam ?: "",
        stateMachine = false,
        machine = null,
        record = null,
        successNextStateId = -1,
        failureNextStateId = -1,
        timeoutNextStateId = -1,
        bindVarName = null,
        tailExpr = null,
        netMode = netMode,
        capturedVars = capturedVars,
    )

    /** 状态机形态构造器。 */
    constructor(
        requestId: String,
        deadline: Long,
        machine: StmtNode.StateMachine,
        record: StateRecord,
        successNextStateId: Int,
        failureNextStateId: Int,
        timeoutNextStateId: Int,
        bindVarName: String?,
        tailExpr: ExprNode?,
    ) : this(
        requestId = requestId,
        deadline = deadline,
        successBody = null,
        successParam = null,
        failBody = null,
        failParam = null,
        timeoutBody = null,
        timeoutParam = null,
        stateMachine = true,
        machine = machine,
        record = record,
        successNextStateId = successNextStateId,
        failureNextStateId = failureNextStateId,
        timeoutNextStateId = timeoutNextStateId,
        bindVarName = bindVarName,
        tailExpr = tailExpr,
        netMode = true,
    )
}
